import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { listMyCommunicationAssignments, patchCommunicationAssignmentProgress } from "../../services/api/communicationLearning";
import { ROUTES } from "../../utils/constants";
import { showErrorToast } from "../../utils/errorHandler";

type Props = {
  /** Called after progress patch so parent can refresh without full page reload */
  onListChange?: () => void;
  /** Increment from parent (e.g. header Refresh) to reload the list */
  refreshSignal?: number;
  /**
   * ``dashboard``: compact "From your teacher" strip for Home (open tasks only, navigate to tutor).
   * ``full``: assignments tab with progress actions.
   */
  variant?: "full" | "dashboard";
};

export const CommunicationAssignmentsPanel: React.FC<Props> = ({
  onListChange,
  refreshSignal = 0,
  variant = "full",
}) => {
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await listMyCommunicationAssignments();
      setItems((r.items as Record<string, unknown>[]) ?? []);
    } catch (e) {
      setItems([]);
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Could not load communication assignments.";
      setLoadError(msg || "Could not load communication assignments.");
      if (variant !== "dashboard") {
        showErrorToast(msg || "Could not load communication assignments.");
      }
    } finally {
      setLoading(false);
    }
  }, [variant]);

  useEffect(() => {
    void load();
  }, [load, refreshSignal]);

  const patchAndRefresh = async (
    assignmentId: string,
    body: { status: string; attempts_delta: number }
  ) => {
    await patchCommunicationAssignmentProgress(assignmentId, body);
    await load();
    onListChange?.();
  };

  const openOnly =
    variant === "dashboard"
      ? items.filter((row) => {
          const a = row as Record<string, unknown>;
          const prog = (a.progress as Record<string, unknown>) || {};
          const s = String(prog.status ?? "not_started").toLowerCase();
          return s !== "completed";
        })
      : items;

  if (variant === "dashboard") {
    if (loading) {
      return (
        <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50">
          <CardContent className="pt-6 pb-6">
            <p className="text-sm text-amber-900/80 dark:text-amber-100/80">Loading teacher assignments…</p>
          </CardContent>
        </Card>
      );
    }
    if (loadError) {
      return (
        <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50">
          <CardContent className="pt-6 pb-6">
            <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          </CardContent>
        </Card>
      );
    }
    if (openOnly.length === 0) {
      return null;
    }
    return (
      <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wide text-amber-900 dark:text-amber-100 font-semibold">
            From your teacher
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-2">
            {openOnly.map((row) => {
              const a = row as Record<string, unknown>;
              const aid = String(a._id ?? "");
              const topicId = String(a.topic_id ?? "").trim();
              const tutorHref = topicId
                ? `${ROUTES.STUDENT_COMMUNICATION_TUTOR}?topic_id=${encodeURIComponent(topicId)}`
                : ROUTES.STUDENT_COMMUNICATION_TUTOR;
              return (
                <li
                  key={aid}
                  className="text-sm text-slate-800 dark:text-slate-100 flex flex-wrap items-center gap-x-3 gap-y-1"
                >
                  <span className="font-medium">{String(a.title ?? "Practice task")}</span>
                  <span className="text-slate-600 dark:text-slate-400">Due {String(a.due_date ?? "—")}</span>
                  <Link to={tutorHref} className="text-primary-700 dark:text-primary-300 font-semibold underline">
                    Practice this topic
                  </Link>
                  <Link
                    to={ROUTES.STUDENT_ASSIGNMENTS_COMMUNICATION_TAB}
                    className="text-xs text-slate-600 dark:text-slate-400 underline"
                  >
                    My Assignments
                  </Link>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Communication practice</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : loadError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No communication assignments for your grade yet. If your teacher just added one, tap Refresh. Open{" "}
            <Link to={ROUTES.STUDENT_COMMUNICATION_TUTOR} className="font-semibold text-primary-600 underline">
              Communication Tutor
            </Link>{" "}
            to practice.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((row) => {
              const a = row as Record<string, unknown>;
              const prog = (a.progress as Record<string, unknown>) || {};
              const aid = String(a._id ?? "");
              const topicId = String(a.topic_id ?? "").trim();
              const tutorHref = topicId
                ? `${ROUTES.STUDENT_COMMUNICATION_TUTOR}?topic_id=${encodeURIComponent(topicId)}`
                : ROUTES.STUDENT_COMMUNICATION_TUTOR;
              return (
                <li
                  key={aid}
                  className="p-4 rounded-xl border border-purple-100 bg-gradient-to-r from-white to-violet-50/50 dark:border-purple-900/40 dark:from-slate-900 dark:to-violet-950/30"
                >
                  <div className="font-medium text-slate-900 dark:text-white">{String(a.title ?? "")}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Due {String(a.due_date ?? "")}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    Status: {String(prog.status ?? "not_started")}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void patchAndRefresh(aid, { status: "in_progress", attempts_delta: 1 })}
                    >
                      Mark in progress
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void patchAndRefresh(aid, { status: "completed", attempts_delta: 0 })}
                    >
                      Mark completed
                    </Button>
                    <Link
                      to={tutorHref}
                      className="text-sm font-semibold text-primary-600 dark:text-primary-400 underline"
                    >
                      Open in Communication Tutor
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
